import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, Space, Typography, Drawer } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  AppstoreOutlined,
  TagsOutlined,
  ShopOutlined,
  CameraOutlined,
  MessageOutlined,
  PictureOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  NotificationOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  HistoryOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './AdminLayout.css';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileDrawerVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check on mount

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/progress',
      icon: <DashboardOutlined />,
      label: 'Progress',
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: 'Users',
    },
    {
      key: '/posts',
      icon: <FileTextOutlined />,
      label: 'Posts',
    },
    {
      key: '/pets',
      icon: <ShopOutlined />,
      label: 'Pet Listings',
    },
    {
      key: '/stories',
      icon: <CameraOutlined />,
      label: 'Stories',
    },
    {
      key: '/comments',
      icon: <MessageOutlined />,
      label: 'Comments',
    },
    {
      key: '/categories',
      icon: <AppstoreOutlined />,
      label: 'Pet Categories',
    },
    {
      key: '/breeds',
      icon: <TagsOutlined />,
      label: 'Breeds',
    },
    {
      key: '/sliders',
      icon: <PictureOutlined />,
      label: 'Sliders',
    },
    {
      key: '/notifications',
      icon: <NotificationOutlined />,
      label: 'Notifications',
    },
    {
      key: '/keywords',
      icon: <FileTextOutlined />,
      label: 'Blocked Keywords',
    },
      {
        key: '/reports',
        icon: <ExclamationCircleOutlined />,
        label: 'Reports',
      },
      {
        key: '/pages',
        icon: <FileTextOutlined />,
        label: 'CMS Pages',
      },
      {
        key: '/faqs',
        icon: <QuestionCircleOutlined />,
        label: 'FAQs',
      },
      {
        key: '/analytics',
        icon: <DashboardOutlined />,
        label: 'Analytics',
      },
      {
        key: '/admin-roles',
        icon: <UserOutlined />,
        label: 'Admin Roles',
      },
      {
        key: '/admin-logs',
        icon: <HistoryOutlined />,
        label: 'Action Logs',
      },
      {
        key: '/settings',
        icon: <SafetyOutlined />,
        label: 'App Settings',
      },
      {
        key: '/geo-restrictions',
        icon: <SafetyOutlined />,
        label: 'Geo Restrictions',
      },
      {
        key: '/user-consents',
        icon: <HistoryOutlined />,
        label: 'User Consents',
      },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    // Close mobile drawer when menu item is clicked
    if (isMobile) {
      setMobileDrawerVisible(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      label: (
        <Space>
          <UserOutlined />
          <span>Profile</span>
        </Space>
      ),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      label: (
        <Space>
          <LogoutOutlined />
          <span>Logout</span>
        </Space>
      ),
      onClick: handleLogout,
    },
  ];

  const menuContent = (
    <>
      <div className="logo">
        Jivbook Admin
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sider */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="dark"
        className="desktop-sider"
        breakpoint="lg"
        collapsedWidth={isMobile ? 0 : 80}
      >
        {menuContent}
      </Sider>

      {/* Mobile Drawer */}
      <Drawer
        title="Jivbook Admin"
        placement="left"
        onClose={() => setMobileDrawerVisible(false)}
        open={mobileDrawerVisible}
        bodyStyle={{ padding: 0 }}
        className="mobile-drawer"
        width={250}
      >
        <div className="drawer-menu-wrapper">
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
          />
        </div>
      </Drawer>

      <Layout>
        <Header className="admin-header">
          <Button
            type="text"
            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => {
              if (isMobile) {
                setMobileDrawerVisible(true);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            className="menu-trigger"
          />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }} className="user-menu">
              <Avatar icon={<UserOutlined />} src={user?.profileImage} />
              {!isMobile && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Text strong>{user?.name || 'Admin'}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{user?.email}</Text>
                </div>
              )}
            </Space>
          </Dropdown>
        </Header>
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;

